import {
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type ReactNode
} from 'react'

import {
  TV_CHART_GROW_STEPS,
  TV_CONTENT_SCALE_FLOOR,
  tvFillScale,
  tvFitScale
} from '../lib/tv-canvas.ts'
import { ChartGrowProvider } from './chart-grow.tsx'

/**
 * The custom property that holds the height the page may fill, in canvas pixels.
 *
 * The canvas sets it on the page box. A page outside the TV has no value.
 */
export const TV_PAGE_HEIGHT = '--tv-page-height'

/** The chart growth step for one page. A new page starts at step 0. */
interface GrowStep {
  page: string | undefined
  step: number
}

/**
 * The scale in force, and the logical canvas size it was measured from.
 */
interface CanvasFit {
  scale: number
  width: number
  height: number
  contentScale: number
}

const INITIAL_FIT: CanvasFit = {
  scale: 1,
  width: 0,
  height: 0,
  contentScale: 1
}

/**
 * Read a unitless number from a custom property.
 *
 * A missing property is 0. The caller then keeps its own fallback.
 *
 * @param element - The element that inherits the property.
 * @param name - The custom property name, including the leading dashes.
 * @returns The number, or 0.
 */
function cssNumber(element: Element, name: string): number {
  const raw = getComputedStyle(element).getPropertyValue(name).trim()
  const value = Number.parseFloat(raw)

  return Number.isFinite(value) ? value : 0
}

/**
 * Read a padding edge.
 *
 * `getComputedStyle` answers a CSS length. A missing length is 0.
 *
 * @param raw - The computed padding.
 * @returns The pixels.
 */
function edge(raw: string): number {
  const value = Number.parseFloat(raw)

  return Number.isFinite(value) ? value : 0
}

/**
 * The layout width that fills the canvas once the content scale applies.
 *
 * @param contentScale - The content scale.
 * @returns A percentage width, or empty for the natural width.
 */
function fillWidth(contentScale: number): string {
  return contentScale < 1 ? `${100 / contentScale}%` : ''
}

/**
 * Whether two measurements are the same scale.
 *
 * @param left - The measurement on screen.
 * @param right - The measurement just taken.
 * @returns True when nothing changed.
 */
function sameFit(left: CanvasFit, right: CanvasFit): boolean {
  return (
    left.scale === right.scale &&
    left.width === right.width &&
    left.height === right.height &&
    left.contentScale === right.contentScale
  )
}

/**
 * TvCanvas — a 1920 by 1080 page, scaled to the screen.
 *
 * The logical size and the content floor come from the theme. The viewport
 * scale fits that canvas. A page taller than the padded canvas scales down
 * again, and stops at the floor. Below the floor the canvas clips. Nothing
 * scrolls. A page that scales down lays out wider by the same factor, so it
 * still fills the canvas width.
 *
 * A header sits above the page at full size. The page fits the room the
 * header leaves, and only the page scales.
 *
 * Charts start grown. When the page still overflows at the floor, the charts
 * step down one growth and the page measures again. The growth only steps
 * down, so it cannot oscillate, and a new `page` starts it again.
 *
 * A full-screen page skips all of that. It fills the whole viewport, with no
 * header, no padding, no letterbox, no scale and no chart growth.
 *
 * The display runtime and its previews share this frame.
 *
 * @param props - Canvas props.
 * @param props.header - The bar above the page. Omit it for none.
 * @param props.page - Which page is up. A change starts the chart growth again.
 * @param props.viewport - A preview's reported CSS viewport. A TV uses the window.
 * @param props.fullscreen - True when the page fills the whole viewport.
 * @param props.children - The page.
 * @returns The letterboxed canvas, or the full-screen page.
 */
export function TvCanvas({
  header,
  page,
  viewport: reportedViewport,
  fullscreen = false,
  children
}: {
  header?: ReactNode
  page?: string
  viewport?: { width: number; height: number }
  fullscreen?: boolean
  children: ReactNode
}): JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const padRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [fit, setFit] = useState<CanvasFit>(INITIAL_FIT)
  const [grow, setGrow] = useState<GrowStep>({ page, step: 0 })
  const growStep = grow.page === page ? grow.step : 0
  const pageRef = useRef(page)
  const fullHeight = reportedViewport
    ? `${reportedViewport.height}px`
    : '100svh'

  useLayoutEffect(() => {
    pageRef.current = page
  })

  useLayoutEffect(() => {
    if (fullscreen) {
      contentRef.current?.style.setProperty(TV_PAGE_HEIGHT, fullHeight)
    }
  }, [fullscreen, fullHeight])

  useLayoutEffect(() => {
    // A full-screen page has no canvas to measure. Leaving that mode mounts a
    // new canvas, so the measure must start again on it.
    if (fullscreen) {
      return () => {}
    }
    const viewport = viewportRef.current
    const canvas = canvasRef.current
    const pad = padRef.current
    const content = contentRef.current
    if (!viewport || !canvas || !pad || !content) {
      return () => {}
    }

    const measure = (): void => {
      const width = canvas.clientWidth || cssNumber(canvas, '--tv-canvas-width')
      const height =
        canvas.clientHeight || cssNumber(canvas, '--tv-canvas-height')
      const scale = tvFitScale(
        viewport.clientWidth,
        viewport.clientHeight,
        width,
        height
      )
      // clientHeight includes the padding. The page uses border-box.
      // Subtracting the padding and the header leaves the box the page may fill.
      const padStyle = getComputedStyle(pad)
      const available =
        (pad.clientHeight || height) -
        edge(padStyle.paddingTop) -
        edge(padStyle.paddingBottom) -
        (headerRef.current?.offsetHeight ?? 0)
      // A page that keeps an aspect ratio, such as a TV dashboard, sizes
      // itself to this height. It must be set before the first measure.
      content.style.setProperty(TV_PAGE_HEIGHT, `${Math.max(available, 0)}px`)
      const themedFloor = cssNumber(canvas, '--tv-content-scale-floor')
      // Each try sets the width and reads the height, one reflow each. The
      // search starts from the natural width every time, so a resize that
      // this width causes measures the same scale again and stops.
      const contentScale = tvFillScale(
        tried => {
          content.style.width = fillWidth(tried)

          return content.scrollHeight
        },
        available,
        themedFloor > 0 ? themedFloor : TV_CONTENT_SCALE_FLOOR
      )
      // A commit from an observer can land after a paint. Write the scale
      // with the width, so no frame shows the wide layout unscaled.
      content.style.width = fillWidth(contentScale)
      content.style.transform = contentScale < 1 ? `scale(${contentScale})` : ''
      const next = { scale, width, height, contentScale }

      setFit(current => (sameFit(current, next) ? current : next))
      // Each measure claims the growth for the page now up, so a page that
      // fits does not leave the last page's step behind for its next turn.
      const shown = pageRef.current
      const overflows = contentScale * content.scrollHeight > available + 1
      setGrow(current => {
        const step = current.page === shown ? current.step : 0
        if (overflows && step < TV_CHART_GROW_STEPS.length - 1) {
          return { page: shown, step: step + 1 }
        }

        return current.page === shown ? current : { page: shown, step }
      })
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(viewport)
    observer.observe(content)
    if (headerRef.current) observer.observe(headerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [fullscreen])

  if (fullscreen) {
    return (
      <div
        ref={viewportRef}
        data-testid="display-screen"
        className="bg-background h-svh w-full overflow-hidden"
        style={
          reportedViewport
            ? { width: reportedViewport.width, height: reportedViewport.height }
            : undefined
        }
      >
        <div
          ref={contentRef}
          data-testid="tv-canvas-content"
          className="h-full w-full overflow-hidden"
        >
          <ChartGrowProvider grow={1}>{children}</ChartGrowProvider>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={viewportRef}
      data-testid="display-screen"
      className="bg-background flex h-svh w-full items-center justify-center overflow-hidden"
      style={
        reportedViewport
          ? { width: reportedViewport.width, height: reportedViewport.height }
          : undefined
      }
    >
      <div
        data-testid="tv-canvas-frame"
        className="shrink-0 overflow-hidden"
        style={{ width: fit.width * fit.scale, height: fit.height * fit.scale }}
      >
        <div
          ref={canvasRef}
          data-testid="tv-canvas"
          className="h-(--tv-canvas-height) w-(--tv-canvas-width) origin-top-left"
          style={{ transform: `scale(${fit.scale})` }}
        >
          <div
            ref={padRef}
            data-testid="tv-canvas-pad"
            className="h-full overflow-hidden p-(--space-6)"
          >
            {header === undefined ? null : (
              <div
                ref={headerRef}
                data-testid="tv-canvas-header"
                className="pb-(--space-3)"
              >
                {header}
              </div>
            )}
            <div
              ref={contentRef}
              data-testid="tv-canvas-content"
              className="origin-top-left"
              style={
                fit.contentScale < 1
                  ? {
                      width: fillWidth(fit.contentScale),
                      transform: `scale(${fit.contentScale})`
                    }
                  : undefined
              }
            >
              <ChartGrowProvider grow={TV_CHART_GROW_STEPS[growStep] ?? 1}>
                {children}
              </ChartGrowProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
